import { create } from 'zustand';
import { getTotalPendingCount } from '../services/offline-queue';

// =============================================================================
// TYPES
// =============================================================================

interface NetworkStore {
  /** Current online/offline state */
  isOnline: boolean;
  /** Whether sync is currently in progress */
  isSyncing: boolean;
  /** Count of pending items to sync */
  pendingCount: number;
  /** Last successful sync timestamp */
  lastSyncTime: Date | null;
  /** Update online state */
  setOnline: (online: boolean) => void;
  /** Update syncing state */
  setSyncing: (syncing: boolean) => void;
  /** Refresh pending count from database */
  updatePendingCount: () => Promise<void>;
  /** Mark sync as complete */
  updateLastSync: () => void;
}

// =============================================================================
// STORE
// =============================================================================

export const useNetworkStore = create<NetworkStore>((set, get) => ({
  isOnline: navigator.onLine,
  isSyncing: false,
  pendingCount: 0,
  lastSyncTime: null,

  setOnline: (online) => {
    set({ isOnline: online });
    if (online) {
      // When coming back online, update pending count
      get().updatePendingCount();
    }
  },

  setSyncing: (syncing) => set({ isSyncing: syncing }),

  updatePendingCount: async () => {
    const count = await getTotalPendingCount();
    set({ pendingCount: count });
  },

  updateLastSync: () => set({ lastSyncTime: new Date() }),
}));

// =============================================================================
// INITIALIZATION
// =============================================================================

// Setup event listeners on app start
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => useNetworkStore.getState().setOnline(true));
  window.addEventListener('offline', () => useNetworkStore.getState().setOnline(false));

  // Initial pending count
  useNetworkStore.getState().updatePendingCount();
}
